import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { getProviderPlanPriceText } from 'common/utility/helpers';

import { formatPrice, numberWithCommas } from 'common/formatters';

import SrMarkdown, {
  SR_MARKDOWN_THEME_UTILITY_PROFIT,
  SR_MARKDOWN_THEME_UTILITY_PROFIT_HELVETICA,
} from 'client/components/SrMarkdown';
import SrText from 'client/components/SrText';
import SrExternalLink from 'client/components/SrExternalLink';
import SrUtilityProfitProviderRetailAmenityPlanCard from './SrUtilityProfitProviderRetailAmenityPlanCard';

import './SrUtilityProfitProviderPlanCard.less';

export const PROVIDER_PLAN_CARD_THEME_DEFAULT = 'default';
export const PROVIDER_PLAN_CARD_THEME_CARRIER = 'carrier';
export const PROVIDER_PLAN_CARD_THEME_AMENITY = 'amenity';
export const PROVIDER_PLAN_CARD_THEME_RETAIL_AMENITY = 'retailAmenity';

const SrUtilityProfitProviderPlanCard = ({
  onClick,
  isActive,
  theme = PROVIDER_PLAN_CARD_THEME_DEFAULT,
  utilityProviderPlan,
  utilityProvider,
  isDefaultInternetPlan,
  utilityPartner,
  internetPlanDescriptionV2,
}) => {
  if (!utilityProviderPlan) {
    return null;
  }

  if (theme === PROVIDER_PLAN_CARD_THEME_RETAIL_AMENITY) {
    return (
      <SrUtilityProfitProviderRetailAmenityPlanCard
        isActive={isActive}
        utilityProviderPlan={utilityProviderPlan}
        utilityPartner={utilityPartner}
        onClick={onClick}
      />
    );
  }

  const PromotionComponent = utilityProviderPlan.termsOfServiceUrl ? SrExternalLink : SrText;

  const providerTheme = utilityProvider?.theme || {};

  const planDescription =
    internetPlanDescriptionV2 === 'updated' &&
    utilityProviderPlan.descriptionVariant
      ? utilityProviderPlan.descriptionVariant
      : utilityProviderPlan.description;

  return (
    <div
      styleName={classnames('providerPlanBox', {
        isActive,
        isCarrierThemeEnabled: theme === PROVIDER_PLAN_CARD_THEME_CARRIER,
        isDefaultInternetPlanOn: isDefaultInternetPlan,
      })}
      style={
        theme === PROVIDER_PLAN_CARD_THEME_CARRIER
          ? {
              borderColor: providerTheme?.primaryColor,
              backgroundColor: providerTheme?.highlightBackgroundColor,
            }
          : {}
      }
      onClick={onClick}
    >
      {!!isDefaultInternetPlan && (
        <div styleName="defaultInternetPlanBox">
          <span styleName="defaultInternetPlanText">Default internet at your home</span>
        </div>
      )}
      {utilityProvider && (
        <div styleName="providerLogoBox">
          {utilityProvider.logoUrl ? (
            <div
              styleName="providerLogo"
              style={{ backgroundImage: `url(${utilityProvider.logoUrl})` }}
            />
          ) : (
            <span
              styleName={classnames('providerNameText', {
                isCarrierThemeEnabled: theme === PROVIDER_PLAN_CARD_THEME_CARRIER,
              })}
            >
              {utilityProvider.title}
            </span>
          )}
        </div>
      )}
      <div styleName="spaceBetweenText">
        <span
          styleName={classnames('titleText', {
            isCarrierThemeEnabled: theme === PROVIDER_PLAN_CARD_THEME_CARRIER,
          })}
        >
          {utilityProviderPlan.title}
        </span>
        {utilityProviderPlan.originalPrice &&
          utilityProviderPlan.originalPrice !== utilityProviderPlan.price &&
          theme !== PROVIDER_PLAN_CARD_THEME_AMENITY && (
            <span
              styleName={classnames('originalPriceText', {
                isCarrierThemeEnabled: theme === PROVIDER_PLAN_CARD_THEME_CARRIER,
                red: isDefaultInternetPlan,
              })}
            >
              {getProviderPlanPriceText({
                price: utilityProviderPlan.originalPrice,
                unit: utilityProviderPlan.priceUnit,
              })}
            </span>
          )}
      </div>
      <div styleName="spaceBetweenText subtitleBox">
        <span
          styleName={classnames('subtitleText', {
            isCarrierThemeEnabled: theme === PROVIDER_PLAN_CARD_THEME_CARRIER,
          })}
        >
          {utilityProviderPlan.internetSpeedMbps
            ? `Speeds up to ${numberWithCommas(utilityProviderPlan.internetSpeedMbps)}Mbps`
            : ''}
        </span>
        {theme !== PROVIDER_PLAN_CARD_THEME_AMENITY && (
          <span
            styleName={classnames('priceText', {
              isCarrierThemeEnabled: theme === PROVIDER_PLAN_CARD_THEME_CARRIER,
            })}
            style={
              theme === PROVIDER_PLAN_CARD_THEME_CARRIER
                ? { color: providerTheme?.primaryColor }
                : {}
            }
          >
            {getProviderPlanPriceText({
              price: utilityProviderPlan.price,
              unit: utilityProviderPlan.priceUnit,
            })}
          </span>
        )}
      </div>
      {theme === PROVIDER_PLAN_CARD_THEME_AMENITY && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', marginTop: '4px' }}>
          <div styleName="priceBox">
            <span styleName="priceIcon">🏷️&nbsp;</span>
            <span styleName="priceText">
              {getProviderPlanPriceText({
                price: utilityProviderPlan.price,
                unit: utilityProviderPlan.priceUnit,
              })}{' '}
              for you.
            </span>
          </div>
          <div styleName="priceBox">
            <span
              styleName={classnames('originalPriceText', {
                isAmenityThemeEnabled: true,
              })}
            >
              {formatPrice(utilityProviderPlan.originalPrice)}
            </span>
            <span styleName="retailPriceText">
              {utilityProviderPlan.priceUnit ? `/${utilityProviderPlan.priceUnit} ` : ''}
              retail price.
            </span>
          </div>
        </div>
      )}
      {planDescription && (
        <div styleName="descriptionBox">
          <SrMarkdown
            source={planDescription}
            theme={
              theme === PROVIDER_PLAN_CARD_THEME_CARRIER
                ? SR_MARKDOWN_THEME_UTILITY_PROFIT_HELVETICA
                : SR_MARKDOWN_THEME_UTILITY_PROFIT
            }
          />
          {utilityProviderPlan.broadbandLabelUrl && (
            <div
              className={classnames('SrMarkdown', 'utilityProfit', {
                helvetica: theme === PROVIDER_PLAN_CARD_THEME_CARRIER,
              })}
            >
              <ul>
                <li
                  onClick={() => {
                    global.open(utilityProviderPlan.broadbandLabelUrl, '_blank');
                  }}
                >
                  <a>View Broadband Fact Label</a>
                </li>
              </ul>
            </div>
          )}
          {!utilityProviderPlan.broadbandLabelUrl && (
            <div
              className={classnames('SrMarkdown', 'utilityProfit', {
                helvetica: theme === PROVIDER_PLAN_CARD_THEME_CARRIER,
              })}
            >
              <ul>
                <li>Broadband Fact Label available before your card is charged</li>
              </ul>
            </div>
          )}
        </div>
      )}
      {utilityProviderPlan.promotion && (
        <div styleName={classnames('spaceBetweenText', 'promotionBox', { isActive })}>
          <span
            styleName={classnames('promotionText', {
              isCarrierThemeEnabled: theme === PROVIDER_PLAN_CARD_THEME_CARRIER,
            })}
          >
            {utilityProviderPlan.promotion}
          </span>
          {utilityProviderPlan.termsOfServiceUrl && (
            <PromotionComponent styleName="smallText" link={utilityProviderPlan.termsOfServiceUrl}>
              See offer details
            </PromotionComponent>
          )}
        </div>
      )}
    </div>
  );
};

SrUtilityProfitProviderPlanCard.propTypes = {
  onClick: PropTypes.func,
  isActive: PropTypes.bool,
  theme: PropTypes.oneOf([
    PROVIDER_PLAN_CARD_THEME_DEFAULT,
    PROVIDER_PLAN_CARD_THEME_CARRIER,
    PROVIDER_PLAN_CARD_THEME_AMENITY,
    PROVIDER_PLAN_CARD_THEME_RETAIL_AMENITY,
  ]),
  utilityProviderPlan: PropTypes.object,
  utilityProvider: PropTypes.object,
  internetPlanDescriptionV2: PropTypes.string,
};

SrUtilityProfitProviderPlanCard.displayName = 'SrUtilityProfitProviderPlanCard';
export default SrUtilityProfitProviderPlanCard;
